import type { Acoustic, Driver, Profile, Signal, SignalKey, Speed, Vocal } from "./types";
import { VERIFIED_TAGS } from "./vocab";

export { VERIFIED_TAGS };

interface Rule {
  tags: Record<string, number>;
  speed?: Speed;
  vocal?: Vocal;
  acoustic?: Acoustic;
  /** Чем выше, тем сильнее правило диктует ограничения. Ночь не спорит со штормом. */
  authority: number;
}

/**
 * Сигнал → вклад в профиль. Порт табличного движка оригинала: упорядоченные
 * приоритеты остались, но выход — взвешенный вектор в словаре площадки, а не
 * одна строка fuzzytags.
 *
 * Теги взяты из выборки каталога (см. vocab.ts), а не придуманы. Тест
 * сторожит, что каждый тег существует и каждое правило способно сработать.
 */
const RULES: Partial<Record<SignalKey, Rule>> = {
  // — то, что человек замечает раньше всего —
  grey_streak:      { tags: { melancholic: 1.0, nostalgic: 0.8, piano: 0.7, lofi: 0.5, sentimental: 0.5 }, speed: "low", acoustic: "acoustic", authority: 9 },
  snow:             { tags: { piano: 1.0, classical: 0.7, newage: 0.6, choral: 0.4, strings: 0.4 }, speed: "verylow", vocal: "instrumental", acoustic: "acoustic", authority: 9 },
  storm:            { tags: { metal: 0.9, drumnbass: 0.8, electricguitar: 0.6, fast: 0.6, energetic: 0.6 }, speed: "high", authority: 9 },
  fog:              { tags: { ambient: 1.0, experimental: 0.7, dark: 0.6, newage: 0.4 }, speed: "low", vocal: "instrumental", authority: 8 },
  heavy_rain:       { tags: { blues: 0.9, rock: 0.6, electricguitar: 0.5, bluesrock: 0.5 }, speed: "medium", authority: 8 },
  rain:             { tags: { blues: 0.9, jazz: 0.7, melancholic: 0.7, saxophone: 0.4, lofi: 0.4 }, speed: "low", authority: 7 },

  // — небо и свет —
  golden_hour:      { tags: { chillout: 1.0, downtempo: 0.8, lounge: 0.7, soft: 0.5 }, speed: "low", authority: 7 },
  clear:            { tags: { happy: 0.7, upbeat: 0.6, indie: 0.5, electropop: 0.4, peaceful: 0.4 }, authority: 5 },
  overcast:         { tags: { ambient: 0.7, downtempo: 0.6, melancholic: 0.5, lofi: 0.4 }, speed: "low", authority: 5 },
  night:            { tags: { ambient: 0.8, chillout: 0.6, lofi: 0.5, meditative: 0.4, soft: 0.4 }, speed: "low", vocal: "instrumental", authority: 6 },

  // — температура —
  hot:              { tags: { summer: 0.9, reggae: 0.7, latin: 0.7, house: 0.6, soul: 0.5, chillout: 0.5 }, speed: "medium", authority: 7 },
  warm:             { tags: { pop: 0.4, indie: 0.5, folk: 0.4, singersongwriter: 0.4 }, authority: 3 },
  cool:             { tags: { acousticguitar: 0.6, folk: 0.5, indie: 0.4, singersongwriter: 0.4 }, authority: 3 },
  cold:             { tags: { melancholic: 0.6, piano: 0.5, strings: 0.4, emotional: 0.4 }, speed: "low", authority: 4 },
  frost:            { tags: { orchestral: 0.9, classical: 0.8, strings: 0.7, choral: 0.5, filmscore: 0.5 }, speed: "low", vocal: "instrumental", authority: 7 },

  // — изменение: то, ради чего всё это —
  temp_drop:        { tags: { melancholic: 0.7, sad: 0.5, emotional: 0.4 }, authority: 4 },
  temp_rise:        { tags: { uplifting: 0.7, happy: 0.6, upbeat: 0.5 }, authority: 4 },
  anomaly_warm:     { tags: { summer: 0.6, latin: 0.4, groovy: 0.3 }, authority: 3 },
  anomaly_cold:     { tags: { nostalgic: 0.6, piano: 0.4, sentimental: 0.3 }, authority: 3 },
  pressure_falling: { tags: { dark: 0.6, experimental: 0.4, cinematic: 0.4 }, authority: 4 },
  pressure_rising:  { tags: { uplifting: 0.5, peaceful: 0.4 }, authority: 3 },
  windy:            { tags: { energetic: 0.5, rock: 0.4, adventure: 0.4, cinematic: 0.3 }, authority: 4 },
  clearing:         { tags: { uplifting: 0.6, peaceful: 0.5, soft: 0.3 }, authority: 3 },
  worsening:        { tags: { melancholic: 0.5, dark: 0.4, cinematic: 0.3 }, authority: 3 },
};

/**
 * Композитные правила: сочетание сигналов, а не каждый по отдельности.
 *
 * Это порт того, что было у оригинала (`:snow AND :wind → snow_wind`) и что
 * при переписывании потерялось. Без них движок был плоской таблицей
 * «сигнал → теги»: ясное небо давало одно и то же и морозным утром, и в зной.
 *
 * `absorbs` гасит вклад составляющих, чтобы «метель» не звучала как «снег»
 * плюс «ветер» — ровно как поглощение в оригинале.
 */
interface Composite extends Rule {
  when: SignalKey[];
  absorbs?: SignalKey[];
  evKey: string;
}

const COMPOSITES: Composite[] = [
  { when: ["snow", "windy"], absorbs: ["snow", "windy"], evKey: "c_blizzard",
    tags: { orchestral: 1.0, cinematic: 0.8, dark: 0.6, strings: 0.6, filmscore: 0.5 },
    speed: "medium", vocal: "instrumental", authority: 11 },

  { when: ["rain", "night"], absorbs: ["night"], evKey: "c_night_rain",
    tags: { lofi: 1.0, chillhop: 0.8, jazz: 0.7, soft: 0.6, saxophone: 0.4 },
    speed: "low", authority: 10 },

  { when: ["grey_streak", "cold"], absorbs: ["cold"], evKey: "c_long_grey",
    tags: { melancholic: 1.0, strings: 0.7, newage: 0.6, emotional: 0.6, piano: 0.6 },
    speed: "verylow", acoustic: "acoustic", authority: 10 },

  { when: ["clear", "hot"], absorbs: ["clear"], evKey: "c_blaze",
    tags: { summer: 1.0, latin: 0.8, house: 0.7, groovy: 0.6, soul: 0.5, saxophone: 0.4 },
    speed: "medium", authority: 10 },

  { when: ["clear", "frost"], absorbs: ["clear", "frost"], evKey: "c_bright_frost",
    tags: { classical: 1.0, choral: 0.8, piano: 0.7, peaceful: 0.6, strings: 0.5 },
    speed: "low", vocal: "instrumental", authority: 10 },

  { when: ["storm", "night"], absorbs: ["night"], evKey: "c_night_storm",
    tags: { dark: 1.0, experimental: 0.8, electronic: 0.6, cinematic: 0.6 },
    speed: "high", vocal: "instrumental", authority: 11 },

  { when: ["rain", "windy"], absorbs: ["windy"], evKey: "c_foul",
    tags: { bluesrock: 1.0, rock: 0.8, electricguitar: 0.7, energetic: 0.5 },
    speed: "medium", authority: 10 },

  { when: ["fog", "cold"], absorbs: ["cold"], evKey: "c_cold_fog",
    tags: { ambient: 1.0, dark: 0.7, experimental: 0.6, newage: 0.5 },
    speed: "verylow", vocal: "instrumental", authority: 10 },

  { when: ["golden_hour", "clear"], absorbs: ["clear"], evKey: "c_golden",
    tags: { chillout: 1.0, lounge: 0.8, downtempo: 0.7, soft: 0.6, romantic: 0.4 },
    speed: "low", authority: 10 },

  { when: ["anomaly_warm", "clear"], absorbs: ["anomaly_warm"], evKey: "c_odd_warm",
    tags: { dreampop: 0.9, nostalgic: 0.8, indie: 0.6, summer: 0.5 },
    authority: 9 },

  { when: ["pressure_falling", "overcast"], absorbs: ["pressure_falling"], evKey: "c_front",
    tags: { cinematic: 0.9, dark: 0.7, ambient: 0.6, filmscore: 0.5 },
    speed: "low", authority: 9 },

  { when: ["temp_drop", "rain"], absorbs: ["temp_drop"], evKey: "c_turn_cold",
    tags: { melancholic: 0.9, blues: 0.7, sad: 0.6, emotional: 0.5 },
    speed: "low", authority: 9 },
];

export const COMPOSITE_LIST = COMPOSITES;

export const RULES_TAG_MAP = RULES;
export const RULES_TAG_LIST: string[] = [
  ...new Set([
    ...Object.values(RULES).flatMap((r) => Object.keys(r!.tags)),
    ...COMPOSITES.flatMap((c) => Object.keys(c.tags)),
  ]),
];

export function toProfile(signals: Signal[]): Profile {
  const tags = new Map<string, number>();
  const drivers: Driver[] = [];
  const present = new Map(signals.map((s) => [s.key, s]));

  let top = {
    authority: -1,
    speed: undefined as Speed | undefined,
    vocal: undefined as Vocal | undefined,
    acoustic: undefined as Acoustic | undefined,
  };

  const apply = (
    rule: Rule,
    intensity: number,
    confidence: number,
    key: SignalKey,
    evKey: string,
    evValue: string,
  ) => {
    const added: string[] = [];
    for (const [tag, w] of Object.entries(rule.tags)) {
      if (!VERIFIED_TAGS.has(tag)) continue;
      tags.set(tag, (tags.get(tag) ?? 0) + w * intensity * confidence);
      added.push(tag);
    }

    const constraints: string[] = [];
    const effective = rule.authority * intensity;
    if (effective > top.authority) {
      top = {
        authority: effective,
        speed: rule.speed ?? top.speed,
        vocal: rule.vocal ?? top.vocal,
        acoustic: rule.acoustic ?? top.acoustic,
      };
      if (rule.speed) constraints.push(`speed:${rule.speed}`);
      if (rule.vocal) constraints.push(`vocal:${rule.vocal}`);
      if (rule.acoustic) constraints.push(`acoustic:${rule.acoustic}`);
    }

    if (added.length) drivers.push({ signal: key, evKey, evValue, tags: added, constraints });
  };

  // Сперва композиты: сочетание сигналов важнее каждого по отдельности,
  // и сработавший композит гасит свои составляющие.
  const absorbed = new Set<SignalKey>();
  const fired = COMPOSITES
    .filter((c) => c.when.every((k) => present.has(k)))
    .sort((a, b) => b.authority - a.authority);

  for (const c of fired) {
    if (c.when.some((k) => absorbed.has(k))) continue;
    const parts = c.when.map((k) => present.get(k)!);
    const intensity = Math.min(...parts.map((p) => p.intensity));
    const confidence = Math.min(...parts.map((p) => p.confidence));
    apply(c, intensity, confidence, c.when[0]!, c.evKey, "");
    for (const k of c.absorbs ?? []) absorbed.add(k);
  }

  // Затем одиночные сигналы — кроме поглощённых композитами.
  const ranked = [...signals]
    .filter((s) => !absorbed.has(s.key))
    .sort((a, b) => {
      const ra = RULES[a.key]?.authority ?? 0;
      const rb = RULES[b.key]?.authority ?? 0;
      return rb * b.intensity - ra * a.intensity;
    });

  for (const s of ranked) {
    const rule = RULES[s.key];
    if (rule) apply(rule, s.intensity, s.confidence, s.key, s.evKey, s.evValue);
  }

  if (tags.size === 0) {
    tags.set("chillout", 1);
    tags.set("ambient", 0.6);
  }

  return { tags, speed: top.speed, vocal: top.vocal, acoustic: top.acoustic, duration: [120, 480], drivers };
}

/**
 * Теги для запроса — по одному от разных сигналов, а не просто топ по весу.
 *
 * Веса концентрируются: в жаркий ясный день первые три места занимали summer,
 * latin и groovy — одна семья. Пул выходил однородным, и разнообразить его на
 * своей стороне было уже нечем: подавление похожести работает только внутри
 * того, что пришло. Беря по тегу от каждого сработавшего правила, мы
 * спрашиваем разные стороны погоды и получаем пул, в котором есть выбор.
 */
export function queryTags(p: Profile, n = 3): string[] {
  const byWeight = [...p.tags.entries()].sort((a, b) => b[1] - a[1]).map(([t]) => t);
  const out: string[] = [];
  const used = new Set<string>();

  for (const d of p.drivers) {
    if (out.length >= n) break;
    // Из каждого сигнала берём его самый весомый тег, ещё не занятый.
    const pick = byWeight.find((t) => d.tags.includes(t) && !used.has(t));
    if (pick) { out.push(pick); used.add(pick); }
  }

  // Сигналов оказалось меньше, чем нужно тегов — добираем по весу.
  for (const t of byWeight) {
    if (out.length >= n) break;
    if (!used.has(t)) { out.push(t); used.add(t); }
  }
  return out;
}

/**
 * Сдвиг профиля для добора кандидатов в радио-режиме: берём следующие по весу
 * теги, чтобы поток не крутился вокруг одних и тех же трёх слов.
 */
export function driftTags(p: Profile, skip: number, n = 3): string[] {
  const all = [...p.tags.entries()].sort((a, b) => b[1] - a[1]).map(([t]) => t);
  const out = all.slice(skip, skip + n);
  return out.length >= 2 ? out : all.slice(0, n);
}
